import React, { useState } from "react";
import { Home, ShoppingBag, Users, Wifi } from "lucide-react";

const PILLARS = [
  {
    id: "habiter",
    icon: Home,
    label: "Habiter",
    tagline: "4 segments résidentiels",
    detail:
      "Social, Économique, Standing et Premium. Une gradation continue de logements — F2 à F6 — pour loger toutes les classes socio-économiques dans un même quartier, sans ségrégation.",
    points: ["F2 → F6", "42% social", "Finitions par segment", "Domotique EKIPTECH"],
  },
  {
    id: "consommer",
    icon: ShoppingBag,
    label: "Consommer",
    tagline: "Commerces & convenience",
    detail:
      "Centres commerciaux modernes, marchés couverts, convenience stores Wayne intégrés aux stations, food courts et espaces artisanaux. Le commerce sous 5 minutes à pied.",
    points: ["Centres commerciaux", "Convenience Wayne", "Marchés modernes", "Food courts"],
  },
  {
    id: "vivre",
    icon: Users,
    label: "Vivre",
    tagline: "Services de proximité",
    detail:
      "Écoles publiques et privées, cliniques de proximité, espaces de co-working, complexes sportifs, allées piétonnes, aires de jeux, mobilité dernier kilomètre. Le quotidien réinventé.",
    points: ["Écoles & crèches", "Cliniques", "Co-working", "Sport & loisirs"],
  },
  {
    id: "connecter",
    icon: Wifi,
    label: "Connecter",
    tagline: "Smart quartier",
    detail:
      "Stations multiservices Wayne, paiement cashless KIPAY, domotique et IoT EKIPTECH, WiFi collectif, vidéo-surveillance intelligente. Une infrastructure numérique orchestrée.",
    points: ["Stations Wayne", "KIPAY cashless", "IoT EKIPTECH", "WiFi collectif"],
  },
];

const Ecosystem = () => {
  const [active, setActive] = useState("habiter");
  const current = PILLARS.find((p) => p.id === active);
  const Icon = current.icon;

  return (
    <section
      id="ecosysteme"
      data-testid="ecosystem-section"
      className="relative bg-[#FAFAF8] py-24 md:py-40 overflow-hidden"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-16">
        <div className="flex items-center gap-3 mb-8">
          <span className="kidh-diamond" />
          <span className="font-body text-[0.7rem] uppercase tracking-[0.32em] text-[#636E72]">
            L'écosystème de quartier
          </span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 items-end mb-16">
          <h2
            className="lg:col-span-8 font-heading font-black text-[#0C2340] text-4xl md:text-6xl leading-[0.95] tracking-tighter"
            data-testid="ecosystem-title"
          >
            Quatre piliers,<br />
            <span className="text-[#E8611A]">un seul quartier.</span>
          </h2>
          <p className="lg:col-span-4 font-body text-[#2D3436] text-base md:text-lg leading-relaxed">
            Habiter, Consommer, Vivre, Connecter — chaque pilier pensé comme
            une infrastructure du quotidien. Cliquez pour explorer.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
          {/* Pillars column */}
          <div className="lg:col-span-5 flex flex-col">
            {PILLARS.map((p) => {
              const isActive = p.id === active;
              const PIcon = p.icon;
              return (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  data-testid={`ecosystem-pillar-${p.id}`}
                  className={`text-left border-t border-[#0C2340]/15 py-6 md:py-8 group transition-all duration-500 ${
                    isActive ? "pl-6" : "pl-0 hover:pl-4"
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <PIcon
                        size={22}
                        strokeWidth={1.4}
                        className={isActive ? "text-[#E8611A]" : "text-[#636E72]"}
                      />
                      <span
                        className={`font-heading font-black text-2xl md:text-4xl tracking-tighter uppercase transition-colors ${
                          isActive ? "text-[#0C2340]" : "text-[#0C2340]/40"
                        }`}
                      >
                        {p.label}
                      </span>
                    </div>
                    <span
                      className={`font-body text-[0.65rem] uppercase tracking-[0.24em] transition-all ${
                        isActive ? "text-[#E8611A] opacity-100" : "text-[#636E72] opacity-60"
                      }`}
                    >
                      {p.tagline}
                    </span>
                  </div>
                </button>
              );
            })}
            <div className="border-t border-[#0C2340]/15" />
          </div>

          {/* Detail panel */}
          <div
            key={active}
            className="lg:col-span-7 bg-[#0A1C33] text-white p-10 md:p-16 relative overflow-hidden animate-fade-up"
            data-testid="ecosystem-detail-panel"
          >
            <div
              aria-hidden
              className="absolute -top-24 -right-24 w-96 h-96 rotate-45 border border-white/10"
            />
            <div
              aria-hidden
              className="absolute -bottom-32 -left-32 w-96 h-96 rotate-45 border border-[#E8611A]/20"
            />
            <div className="relative">
              <Icon size={40} strokeWidth={1.2} className="text-[#E8611A] mb-6" />
              <div className="font-body text-[0.65rem] uppercase tracking-[0.32em] text-white/60 mb-4">
                {current.tagline}
              </div>
              <h3 className="font-heading font-black text-white text-4xl md:text-6xl tracking-tighter mb-8 leading-none">
                {current.label}<span className="text-[#E8611A]">.</span>
              </h3>
              <p className="font-body text-white/85 text-base md:text-lg leading-relaxed mb-10 max-w-xl">
                {current.detail}
              </p>
              <div className="grid grid-cols-2 gap-4">
                {current.points.map((pt) => (
                  <div
                    key={pt}
                    className="flex items-center gap-3 border-t border-white/15 pt-4"
                  >
                    <span className="kidh-diamond" />
                    <span className="font-body text-white/90 text-sm">{pt}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Ecosystem;
